import React, { useState, useEffect, useMemo, useRef } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { useNavigate } from "react-router-dom";
import {
  FiPlus, FiSearch, FiLayers, FiChevronLeft, FiChevronRight,
  FiMaximize2, FiBox, FiDollarSign, FiX, FiGrid,
  FiList, FiDownload, FiShare2, FiAlertTriangle, FiEdit,
  FiCopy, FiCheck, FiChevronsUp, FiChevronsDown,
  FiStar, FiShoppingCart, FiTag, FiPackage, FiZap,
} from "react-icons/fi";
import { baseurl, imgurl } from "../../baseurl";
import axios from "axios";

/* ─── CSS ─── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

:root {
  --pc-teal:      #0a5556;
  --pc-teal-mid:  #0c7172;
  --pc-teal-lite: #14b8a6;
  --pc-gold:      #FFD166;
  --pc-bg:        #eef6f6;
  --pc-white:     #ffffff;
  --pc-border:    rgba(10,85,86,0.10);
  --pc-text:      #0f2a2a;
  --pc-muted:     #5a8080;
  --pc-danger:    #ef4444;
  --pc-success:   #10b981;
  --pc-info:      #3b82f6;
}

@keyframes pc__fadeUp   { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
@keyframes pc__shimmer  { 0%{background-position:-600px 0} 100%{background-position:600px 0} }
@keyframes pc__pop      { 0%{transform:scale(0.85);opacity:0} 60%{transform:scale(1.08)} 100%{transform:scale(1);opacity:1} }
@keyframes pc__spin     { to{transform:rotate(360deg)} }
@keyframes pc__modalIn  { from{opacity:0;transform:scale(0.94) translateY(10px)} to{opacity:1;transform:scale(1) translateY(0)} }

.pc__wrap {
  font-family:'DM Sans',sans-serif;
  padding:22px 22px 40px;
  background:var(--pc-bg);
  min-height:100vh;
  animation:pc__fadeUp 0.4s ease both;
}

/* ── header ── */
.pc__hd { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; margin-bottom:20px; flex-wrap:wrap; }
.pc__hd-left { display:flex; align-items:center; gap:14px; }
.pc__hd-icon {
  width:52px; height:52px; border-radius:16px; flex-shrink:0;
  background:linear-gradient(135deg,var(--pc-teal),var(--pc-teal-lite));
  display:flex; align-items:center; justify-content:center;
  font-size:22px; color:#fff; box-shadow:0 6px 20px rgba(10,85,86,0.30);
}
.pc__hd-title { font-family:'Sora',sans-serif; font-size:21px; font-weight:800; color:var(--pc-text); letter-spacing:-0.03em; margin:0 0 3px; }
.pc__hd-sub   { font-size:12.5px; color:var(--pc-muted); margin:0; }
.pc__hd-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }

.pc__cta {
  display:inline-flex; align-items:center; gap:8px; padding:10px 18px;
  border-radius:12px; border:none; cursor:pointer;
  font-size:13px; font-family:'Sora',sans-serif; font-weight:700; color:#fff;
  background:linear-gradient(135deg,var(--pc-teal),var(--pc-teal-lite));
  box-shadow:0 4px 14px rgba(10,85,86,0.28); transition:transform 0.14s, box-shadow 0.18s;
}
.pc__cta:hover { transform:translateY(-1px); box-shadow:0 8px 22px rgba(10,85,86,0.34); }
.pc__btn-outline {
  display:inline-flex; align-items:center; gap:6px; padding:9px 14px;
  border-radius:11px; cursor:pointer; font-size:12.5px;
  font-family:'Sora',sans-serif; font-weight:700; color:var(--pc-teal);
  background:rgba(10,85,86,0.07); border:1.5px solid rgba(10,85,86,0.18);
  transition:all 0.15s; white-space:nowrap;
}
.pc__btn-outline:hover { background:rgba(10,85,86,0.13); }
.pc__btn-outline--gold { color:#b45309; background:rgba(255,209,102,0.12); border-color:rgba(255,209,102,0.4); }
.pc__btn-outline--gold:hover { background:rgba(255,209,102,0.22); }

/* ── stats ── */
.pc__stats { display:flex; gap:12px; margin-bottom:20px; flex-wrap:wrap; }
.pc__stat {
  flex:1; min-width:140px; background:var(--pc-white); border:1px solid var(--pc-border);
  border-radius:16px; padding:16px 18px; box-shadow:0 2px 10px rgba(10,85,86,0.05);
  display:flex; align-items:center; gap:13px; position:relative; overflow:hidden;
}
.pc__stat::before {
  content:''; position:absolute; top:0; left:0; right:0; height:3px;
  background:linear-gradient(90deg,var(--pc-teal),var(--pc-teal-lite),var(--pc-gold));
}
.pc__stat-icon { width:42px; height:42px; border-radius:12px; flex-shrink:0; background:linear-gradient(135deg,var(--pc-teal),var(--pc-teal-lite)); display:flex; align-items:center; justify-content:center; font-size:18px; color:#fff; box-shadow:0 4px 12px rgba(10,85,86,0.22); }
.pc__stat-icon--danger { background:linear-gradient(135deg,#c0392b,var(--pc-danger)); }
.pc__stat-icon--gold   { background:linear-gradient(135deg,#b45309,var(--pc-gold)); }
.pc__stat-lbl { font-size:11px; color:var(--pc-muted); font-weight:500; margin-bottom:2px; }
.pc__stat-val { font-family:'Sora',sans-serif; font-size:16px; font-weight:800; color:var(--pc-text); }

/* ── controls bar ── */
.pc__controls-bar {
  background:var(--pc-white); border:1px solid var(--pc-border); border-radius:16px;
  padding:14px 18px; margin-bottom:20px;
  display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap;
  box-shadow:0 2px 10px rgba(10,85,86,0.04);
}
.pc__controls-left  { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.pc__controls-right { display:flex; align-items:center; gap:8px; }
.pc__search {
  display:flex; align-items:center; gap:8px; background:#f3fafa;
  border:1.5px solid rgba(10,85,86,0.14); border-radius:10px; padding:8px 13px;
  transition:all 0.18s; width:240px;
}
.pc__search:focus-within { border-color:var(--pc-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }
.pc__search-icon  { color:var(--pc-muted); font-size:14px; flex-shrink:0; }
.pc__search-input { border:none; outline:none; background:transparent; font-size:13px; font-family:'DM Sans',sans-serif; color:var(--pc-text); flex:1; min-width:0; }
.pc__search-input::placeholder { color:#9ababa; }
.pc__type-select {
  padding:8px 28px 8px 12px; font-size:12.5px; color:var(--pc-text); background:#f3fafa;
  border:1.5px solid rgba(10,85,86,0.14); border-radius:10px; outline:none; cursor:pointer; appearance:none;
  background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%235a8080' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat:no-repeat; background-position:right 9px center;
}
.pc__type-select:focus { border-color:var(--pc-teal); box-shadow:0 0 0 3px rgba(10,85,86,0.09); background:#fff; }
.pc__filter-pill {
  display:inline-flex; align-items:center; gap:5px; font-size:11.5px;
  font-family:'Sora',sans-serif; font-weight:700;
  border-radius:50px; padding:4px 10px; cursor:pointer;
  background:rgba(10,85,86,0.07); color:var(--pc-teal);
  border:1.5px solid rgba(10,85,86,0.16); transition:all 0.15s;
}
.pc__filter-pill:hover { background:rgba(10,85,86,0.14); }
.pc__filter-pill--active { background:var(--pc-teal); color:#fff; border-color:var(--pc-teal); }
.pc__results-count { font-size:12px; color:var(--pc-muted); font-family:'Sora',sans-serif; font-weight:600; white-space:nowrap; }
.pc__view-toggle { display:flex; gap:3px; background:rgba(10,85,86,0.06); padding:3px; border-radius:10px; }
.pc__view-btn-toggle {
  width:32px; height:30px; border-radius:8px; border:none; cursor:pointer;
  display:flex; align-items:center; justify-content:center; font-size:14px;
  color:var(--pc-muted); background:transparent; transition:all 0.15s;
}
.pc__view-btn-toggle--active { background:var(--pc-teal); color:#fff; box-shadow:0 2px 8px rgba(10,85,86,0.22); }

/* ────────────────────────────────────────────
   GRID VIEW
──────────────────────────────────────────── */
.pc__grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(268px,1fr)); gap:16px; margin-bottom:24px; }
.pc__card {
  background:var(--pc-white); border-radius:18px; border:1px solid var(--pc-border);
  overflow:hidden; position:relative; box-shadow:0 2px 12px rgba(10,85,86,0.06);
  transition:transform 0.22s cubic-bezier(0.34,1.3,0.64,1), box-shadow 0.22s;
  display:flex; flex-direction:column; cursor:pointer;
}
.pc__card::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:linear-gradient(90deg,var(--pc-teal),var(--pc-teal-lite),var(--pc-gold)); z-index:2; }
.pc__card:hover { transform:translateY(-5px); box-shadow:0 16px 40px rgba(10,85,86,0.16); }
.pc__card--lowstock { border-left:4px solid var(--pc-danger); }
.pc__img-wrap { height:168px; overflow:hidden; position:relative; background:#e8f4f4; flex-shrink:0; }
.pc__img { width:100%; height:100%; object-fit:cover; transition:transform 0.5s ease; display:block; }
.pc__card:hover .pc__img { transform:scale(1.07); }
.pc__img-overlay { position:absolute; inset:0; background:rgba(10,30,30,0.58); backdrop-filter:blur(3px); display:flex; align-items:center; justify-content:center; gap:8px; opacity:0; transition:opacity 0.22s; }
.pc__card:hover .pc__img-overlay { opacity:1; }
.pc__overlay-btn { display:inline-flex; align-items:center; gap:5px; padding:7px 14px; border-radius:9px; border:none; cursor:pointer; font-size:12px; font-family:'Sora',sans-serif; font-weight:700; transition:transform 0.14s; }
.pc__overlay-btn--view { background:#fff; color:var(--pc-text); }
.pc__overlay-btn--edit { background:rgba(255,209,102,0.9); color:#7c4f00; }
.pc__overlay-btn:hover { transform:scale(1.06); }
.pc__type-tag { position:absolute; top:10px; left:10px; z-index:3; font-size:10px; font-family:'Sora',sans-serif; font-weight:800; letter-spacing:0.06em; text-transform:uppercase; padding:3px 9px; border-radius:6px; }
.pc__type-tag--product { background:rgba(10,85,86,0.88); color:#fff; }
.pc__type-tag--service  { background:rgba(37,99,235,0.88); color:#fff; }
.pc__lowstock-badge { position:absolute; top:10px; right:10px; z-index:3; font-size:9.5px; font-family:'Sora',sans-serif; font-weight:800; padding:3px 8px; border-radius:6px; background:rgba(239,68,68,0.92); color:#fff; display:flex; align-items:center; gap:3px; }
.pc__card-body { padding:15px; display:flex; flex-direction:column; gap:10px; flex:1; }
.pc__prod-name { font-family:'Sora',sans-serif; font-size:14px; font-weight:800; color:var(--pc-text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; margin:0; }
.pc__prod-hsn { font-size:11px; color:#9ababa; font-family:'Sora',sans-serif; }
.pc__prod-stats { display:flex; justify-content:space-between; align-items:flex-end; border-top:1px solid var(--pc-border); padding-top:10px; }
.pc__sg { display:flex; flex-direction:column; gap:1px; }
.pc__sg-lbl { font-size:9.5px; font-family:'Sora',sans-serif; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; color:var(--pc-muted); }
.pc__sg-val { font-family:'Sora',sans-serif; font-size:14px; font-weight:800; color:var(--pc-text); }
.pc__sg-val--danger { color:var(--pc-danger); }
.pc__sg-val--muted  { color:var(--pc-muted); }
.pc__sg-val--teal   { color:var(--pc-teal); }

/* ────────────────────────────────────────────
   LIST VIEW (table)
──────────────────────────────────────────── */
.pc__list-wrap { background:var(--pc-white); border-radius:18px; border:1px solid var(--pc-border); overflow:hidden; margin-bottom:24px; box-shadow:0 2px 12px rgba(10,85,86,0.05); position:relative; }
.pc__list-wrap::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:linear-gradient(90deg,var(--pc-teal),var(--pc-teal-lite),var(--pc-gold)); }
.pc__list-table { width:100%; border-collapse:collapse; }
.pc__list-table thead th { font-family:'Sora',sans-serif; font-size:10.5px; font-weight:700; letter-spacing:0.09em; text-transform:uppercase; color:var(--pc-teal); background:rgba(10,85,86,0.04); padding:12px 16px; white-space:nowrap; border-bottom:1px solid var(--pc-border); cursor:pointer; user-select:none; }
.pc__list-table thead th:hover { color:var(--pc-teal-lite); }
.pc__sort-icon { display:inline-flex; vertical-align:middle; margin-left:3px; opacity:.35; }
.pc__sort-icon--on { opacity:1; color:var(--pc-teal-lite); }
.pc__list-table tbody tr { border-bottom:1px solid rgba(10,85,86,0.05); transition:background 0.13s; }
.pc__list-table tbody tr:last-child { border-bottom:none; }
.pc__list-table tbody tr:hover { background:rgba(10,85,86,0.025); }
.pc__list-table tbody td { padding:12px 16px; font-size:13px; color:var(--pc-text); vertical-align:middle; }
.pc__list-img { width:38px; height:38px; border-radius:10px; object-fit:cover; border:1.5px solid var(--pc-border); flex-shrink:0; }
.pc__list-prod-cell { display:flex; align-items:center; gap:11px; }
.pc__list-name { font-size:13px; font-weight:600; color:var(--pc-text); margin-bottom:2px; }
.pc__list-uuid { font-size:10px; color:#9ababa; font-family:'Sora',sans-serif; }
.pc__list-price { font-family:'Sora',sans-serif; font-size:13.5px; font-weight:800; color:var(--pc-text); text-align:right; }
.pc__list-stock { font-family:'Sora',sans-serif; font-size:13px; font-weight:700; text-align:center; }
.pc__list-stock--ok  { color:var(--pc-success); }
.pc__list-stock--low { color:var(--pc-danger); }
.pc__list-stock--na  { color:#b0cece; }
.pc__list-actions { display:flex; align-items:center; gap:5px; justify-content:flex-end; }
.pc__list-action-btn { width:30px; height:30px; border-radius:9px; border:1.5px solid var(--pc-border); cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:13px; color:var(--pc-muted); background:transparent; transition:all 0.14s; }
.pc__list-action-btn--view:hover { background:rgba(10,85,86,0.09); color:var(--pc-teal); border-color:rgba(10,85,86,0.22); }
.pc__list-action-btn--edit:hover { background:rgba(255,209,102,0.14); color:#b45309; border-color:rgba(255,209,102,0.4); }
.pc__type-badge { display:inline-flex; align-items:center; gap:4px; font-size:11px; font-weight:700; font-family:'Sora',sans-serif; border-radius:50px; padding:3px 10px; white-space:nowrap; }
.pc__type-badge--product { background:rgba(10,85,86,0.09); color:var(--pc-teal); border:1px solid rgba(10,85,86,0.18); }
.pc__type-badge--service  { background:rgba(59,130,246,0.09); color:#2563eb; border:1px solid rgba(59,130,246,0.20); }

/* ────────────────────────────────────────────
   AMAZON-STYLE VIEW
──────────────────────────────────────────── */
.pc__amz-wrap { display:flex; flex-direction:column; gap:12px; margin-bottom:24px; }

.pc__amz-card {
  background:var(--pc-white); border-radius:16px; border:1px solid var(--pc-border);
  box-shadow:0 2px 10px rgba(10,85,86,0.05);
  display:grid; grid-template-columns:180px 1fr auto;
  overflow:hidden; position:relative; transition:box-shadow 0.22s, border-color 0.22s;
}
.pc__amz-card::before { content:''; position:absolute; top:0; left:0; bottom:0; width:3px; background:linear-gradient(180deg,var(--pc-teal),var(--pc-teal-lite),var(--pc-gold)); }
.pc__amz-card:hover { box-shadow:0 8px 28px rgba(10,85,86,0.13); border-color:rgba(10,85,86,0.22); }
.pc__amz-card--lowstock { border-color:rgba(239,68,68,0.30); }
.pc__amz-card--lowstock::before { background:linear-gradient(180deg,var(--pc-danger),#fb7185); }

/* image column */
.pc__amz-img-col { position:relative; background:#e8f4f4; overflow:hidden; }
.pc__amz-img { width:100%; height:100%; min-height:160px; object-fit:cover; transition:transform 0.4s ease; display:block; }
.pc__amz-card:hover .pc__amz-img { transform:scale(1.04); }
.pc__amz-img-badge {
  position:absolute; top:8px; left:8px;
  font-size:9.5px; font-family:'Sora',sans-serif; font-weight:800;
  text-transform:uppercase; letter-spacing:0.06em;
  padding:3px 8px; border-radius:6px; z-index:2;
}
.pc__amz-img-badge--product { background:rgba(10,85,86,0.88); color:#fff; }
.pc__amz-img-badge--service  { background:rgba(37,99,235,0.88); color:#fff; }
.pc__amz-img-badge--low  { background:rgba(239,68,68,0.9); color:#fff; top:32px; }

/* center / main info column */
.pc__amz-info { padding:18px 20px; display:flex; flex-direction:column; justify-content:space-between; gap:10px; border-left:1px solid var(--pc-border); }
.pc__amz-name {
  font-family:'Sora',sans-serif; font-size:15.5px; font-weight:800; color:var(--pc-text);
  line-height:1.3; margin:0 0 3px;
  display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;
}
.pc__amz-name:hover { color:var(--pc-teal); cursor:pointer; }
.pc__amz-meta { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.pc__amz-meta-chip {
  display:inline-flex; align-items:center; gap:4px;
  font-size:11px; font-family:'Sora',sans-serif; font-weight:700;
  border-radius:6px; padding:3px 9px; white-space:nowrap;
}
.pc__amz-meta-chip--hsn  { background:rgba(10,85,86,0.07); color:var(--pc-teal); border:1px solid rgba(10,85,86,0.14); }
.pc__amz-meta-chip--unit { background:rgba(59,130,246,0.07); color:#2563eb; border:1px solid rgba(59,130,246,0.16); }
.pc__amz-meta-chip--gst  { background:rgba(255,209,102,0.14); color:#92611a; border:1px solid rgba(255,209,102,0.35); }
.pc__amz-stars { display:flex; align-items:center; gap:4px; }
.pc__amz-star { color:#f59e0b; font-size:13px; }
.pc__amz-star--empty { color:#d1d5db; }
.pc__amz-desc {
  font-size:12.5px; color:var(--pc-muted); line-height:1.55;
  display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;
}
.pc__amz-stock-row { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
.pc__amz-instock  { display:inline-flex; align-items:center; gap:5px; font-size:12px; font-family:'Sora',sans-serif; font-weight:700; color:var(--pc-success); }
.pc__amz-lowstock { display:inline-flex; align-items:center; gap:5px; font-size:12px; font-family:'Sora',sans-serif; font-weight:700; color:var(--pc-danger); }
.pc__amz-stock-qty {
  font-size:11.5px; font-family:'Sora',sans-serif; font-weight:600; color:var(--pc-muted);
  background:rgba(10,85,86,0.05); padding:2px 9px; border-radius:50px;
  border:1px solid var(--pc-border);
}
.pc__amz-uuid { font-size:10.5px; color:#b0cece; font-family:'Sora',sans-serif; letter-spacing:0.04em; }

/* right / price+action column */
.pc__amz-action-col {
  min-width:170px; padding:18px 20px;
  display:flex; flex-direction:column; align-items:flex-end; justify-content:space-between;
  border-left:1px solid var(--pc-border); background:rgba(10,85,86,0.015);
}
.pc__amz-price-wrap { text-align:right; }
.pc__amz-price-lbl { font-size:10.5px; font-family:'Sora',sans-serif; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; color:var(--pc-muted); margin-bottom:3px; }
.pc__amz-price { font-family:'Sora',sans-serif; font-size:22px; font-weight:800; color:var(--pc-teal); letter-spacing:-0.03em; }
.pc__amz-price-gst { font-size:10.5px; color:var(--pc-muted); font-family:'Sora',sans-serif; margin-top:2px; }
.pc__amz-btns { display:flex; flex-direction:column; gap:7px; width:100%; }
.pc__amz-btn-view {
  width:100%; padding:9px; border-radius:10px; border:none; cursor:pointer;
  font-size:12.5px; font-family:'Sora',sans-serif; font-weight:700; color:#fff;
  background:linear-gradient(135deg,var(--pc-teal),var(--pc-teal-lite));
  box-shadow:0 3px 10px rgba(10,85,86,0.22); transition:transform 0.14s, box-shadow 0.18s;
  display:flex; align-items:center; justify-content:center; gap:6px;
}
.pc__amz-btn-view:hover { transform:translateY(-1px); box-shadow:0 6px 16px rgba(10,85,86,0.30); }
.pc__amz-btn-edit {
  width:100%; padding:8px; border-radius:10px; cursor:pointer;
  font-size:12.5px; font-family:'Sora',sans-serif; font-weight:700;
  color:#92611a; background:rgba(255,209,102,0.14);
  border:1.5px solid rgba(255,209,102,0.40); transition:all 0.15s;
  display:flex; align-items:center; justify-content:center; gap:6px;
}
.pc__amz-btn-edit:hover { background:rgba(255,209,102,0.26); }
.pc__amz-valuation {
  font-size:11px; color:var(--pc-muted); font-family:'Sora',sans-serif;
  text-align:right; border-top:1px solid var(--pc-border); padding-top:6px; width:100%;
}
.pc__amz-valuation span { font-weight:700; color:var(--pc-text); }

/* skeleton – amazon style */
.pc__amz-skel {
  background:var(--pc-white); border-radius:16px; border:1px solid var(--pc-border);
  display:grid; grid-template-columns:180px 1fr auto; overflow:hidden;
  box-shadow:0 2px 10px rgba(10,85,86,0.04);
}
.pc__amz-skel-img { background:linear-gradient(90deg,#e8f4f4 0%,#d0eded 50%,#e8f4f4 100%); background-size:600px 100%; animation:pc__shimmer 1.4s linear infinite; min-height:160px; }
.pc__amz-skel-info { padding:18px; display:flex; flex-direction:column; gap:10px; border-left:1px solid var(--pc-border); }
.pc__amz-skel-action { min-width:170px; padding:18px; border-left:1px solid var(--pc-border); display:flex; flex-direction:column; gap:10px; }

/* ── skeleton shared ── */
.pc__skel {
  border-radius:8px;
  background:linear-gradient(90deg,#e8f4f4 0%,#d0eded 50%,#e8f4f4 100%);
  background-size:600px 100%; animation:pc__shimmer 1.4s linear infinite;
}
.pc__skel-card { background:var(--pc-white); border-radius:18px; border:1px solid var(--pc-border); overflow:hidden; }
.pc__skel-img  { height:168px; }

/* ── empty ── */
.pc__empty { padding:60px 20px; text-align:center; grid-column:1/-1; }
.pc__empty-icon { width:60px; height:60px; border-radius:18px; margin:0 auto 14px; background:rgba(10,85,86,0.07); display:flex; align-items:center; justify-content:center; font-size:26px; }
.pc__empty-title { font-family:'Sora',sans-serif; font-size:16px; font-weight:700; color:var(--pc-text); margin-bottom:5px; }
.pc__empty-sub   { font-size:13px; color:var(--pc-muted); }

/* ── pagination ── */
.pc__pagination { display:flex; align-items:center; justify-content:center; gap:8px; }
.pc__pg-info { font-family:'Sora',sans-serif; font-size:12.5px; font-weight:700; color:var(--pc-text); background:var(--pc-white); padding:8px 18px; border-radius:50px; border:1px solid var(--pc-border); box-shadow:0 2px 8px rgba(10,85,86,0.07); }
.pc__pg-btn { width:36px; height:36px; border-radius:50%; border:1.5px solid var(--pc-border); background:var(--pc-white); cursor:pointer; display:flex; align-items:center; justify-content:center; color:var(--pc-muted); box-shadow:0 2px 8px rgba(10,85,86,0.07); transition:all 0.15s; }
.pc__pg-btn:hover:not(:disabled) { border-color:var(--pc-teal); color:var(--pc-teal); background:rgba(10,85,86,0.05); }
.pc__pg-btn:disabled { opacity:.35; cursor:not-allowed; }

/* ── DETAIL MODAL ── */
.pc__modal-overlay { position:fixed; inset:0; z-index:1050; background:rgba(10,30,30,0.58); backdrop-filter:blur(6px); display:flex; align-items:center; justify-content:center; padding:20px; animation:pc__fadeUp 0.22s ease both; }
.pc__modal { background:var(--pc-white); border-radius:24px; width:100%; max-width:680px; box-shadow:0 30px 80px rgba(10,85,86,0.24); overflow:hidden; position:relative; animation:pc__modalIn 0.28s cubic-bezier(0.34,1.3,0.64,1) both; }
.pc__modal::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:linear-gradient(90deg,var(--pc-teal),var(--pc-teal-lite),var(--pc-gold)); }
.pc__modal-hd { display:flex; align-items:center; justify-content:space-between; padding:18px 24px 14px; border-bottom:1px solid var(--pc-border); }
.pc__modal-close { width:32px; height:32px; border-radius:9px; border:none; cursor:pointer; background:rgba(10,85,86,0.07); color:var(--pc-muted); display:flex; align-items:center; justify-content:center; transition:all 0.14s; }
.pc__modal-close:hover { background:rgba(239,68,68,0.1); color:var(--pc-danger); }
.pc__modal-body { display:grid; grid-template-columns:220px 1fr; gap:0; max-height:75vh; overflow-y:auto; }
.pc__modal-img { width:100%; height:100%; min-height:220px; object-fit:cover; }
.pc__modal-info { padding:22px; display:flex; flex-direction:column; gap:14px; }
.pc__modal-row { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; padding:8px 0; border-bottom:1px solid var(--pc-border); }
.pc__modal-row:last-child { border-bottom:none; }
.pc__modal-lbl { font-size:11px; font-family:'Sora',sans-serif; font-weight:700; letter-spacing:0.07em; text-transform:uppercase; color:var(--pc-muted); flex-shrink:0; }
.pc__modal-val { font-size:13.5px; font-weight:600; color:var(--pc-text); text-align:right; }
.pc__modal-val--teal { color:var(--pc-teal); font-family:'Sora',sans-serif; font-weight:800; font-size:15px; }
.pc__modal-ft { display:flex; align-items:center; justify-content:flex-end; gap:8px; padding:14px 22px; border-top:1px solid var(--pc-border); background:rgba(10,85,86,0.02); }

/* ── SHARE modal ── */
.pc__share-modal { background:var(--pc-white); border-radius:24px; width:100%; max-width:440px; box-shadow:0 24px 60px rgba(10,85,86,0.22); overflow:hidden; position:relative; animation:pc__modalIn 0.28s cubic-bezier(0.34,1.3,0.64,1) both; }
.pc__share-modal::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:linear-gradient(90deg,var(--pc-teal),var(--pc-teal-lite),var(--pc-gold)); }
.pc__share-body { padding:24px; }
.pc__share-link-row { display:flex; align-items:center; gap:8px; }
.pc__share-link { flex:1; padding:9px 12px; background:#f3fafa; border:1.5px solid var(--pc-border); border-radius:10px; font-size:12px; color:var(--pc-muted); font-family:'Sora',sans-serif; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; outline:none; }
.pc__copy-btn { padding:9px 14px; border-radius:10px; border:none; cursor:pointer; font-size:12px; font-family:'Sora',sans-serif; font-weight:700; color:#fff; background:var(--pc-teal); display:flex; align-items:center; gap:5px; transition:background 0.15s; }
.pc__copy-btn:hover { background:var(--pc-teal-mid); }
.pc__copy-btn--success { background:var(--pc-success); }

/* toast */
.pc__toast { position:fixed; bottom:28px; right:28px; z-index:2000; background:var(--pc-text); color:#fff; border-radius:12px; padding:12px 20px; font-size:13px; font-family:'Sora',sans-serif; font-weight:700; box-shadow:0 8px 30px rgba(0,0,0,0.25); display:flex; align-items:center; gap:8px; animation:pc__pop 0.3s ease both; }
.pc__toast--success { background:var(--pc-success); }

.pc__spinner { width:14px; height:14px; border-radius:50%; border:2px solid rgba(10,85,86,0.2); border-top-color:var(--pc-teal); animation:pc__spin 0.7s linear infinite; }

@media(max-width:860px){
  .pc__amz-card { grid-template-columns:130px 1fr; }
  .pc__amz-action-col { display:none; }
}
@media(max-width:700px){
  .pc__search{ width:100%; }
  .pc__controls-bar{ flex-direction:column; align-items:stretch; }
  .pc__modal-body{ grid-template-columns:1fr; }
  .pc__modal-img{ height:200px; }
  .pc__amz-card { grid-template-columns:1fr; }
  .pc__amz-img-col { height:160px; }
}
`;

/* ─── Helpers ─── */
const formatCurrency = (v) =>
  Number(v).toLocaleString("en-IN", { style:"currency", currency:"INR", maximumFractionDigits:0 });
const formatCurrencyFull = (v) =>
  "₹" + Number(v).toLocaleString("en-IN", { minimumFractionDigits:2 });

/* ─── Fake stars (visual only) ─── */
const Stars = ({ rate }) => {
  const score = rate > 0 ? Math.min(5, Math.max(3, 3 + (rate % 3))) : 4;
  return (
    <div className="pc__amz-stars">
      {[1,2,3,4,5].map(i => (
        <FiStar key={i} size={12} className={i <= score ? "pc__amz-star" : "pc__amz-star--empty"} fill={i <= score ? "#f59e0b" : "none"} />
      ))}
      <span style={{ fontSize:11, color:"var(--pc-muted)", fontFamily:"Sora,sans-serif", fontWeight:600 }}>{score}.0</span>
    </div>
  );
};

/* ─── Skeletons ─── */
const GridSkeleton = () => (
  <div className="pc__skel-card">
    <div className="pc__skel pc__skel-img" />
    <div style={{ padding:15, display:"flex", flexDirection:"column", gap:10 }}>
      <div className="pc__skel" style={{ height:13, width:"70%" }} />
      <div className="pc__skel" style={{ height:10, width:"40%" }} />
      <div style={{ display:"flex", justifyContent:"space-between", paddingTop:8, borderTop:"1px solid rgba(10,85,86,0.07)" }}>
        <div className="pc__skel" style={{ height:14, width:60 }} />
        <div className="pc__skel" style={{ height:14, width:50 }} />
      </div>
    </div>
  </div>
);

const ListSkeleton = () => Array.from({ length: 6 }).map((_, i) => (
  <tr key={i}>
    <td>
      <div style={{ display:"flex", alignItems:"center", gap:11 }}>
        <div className="pc__skel" style={{ width:38, height:38, borderRadius:10, flexShrink:0 }} />
        <div>
          <div className="pc__skel" style={{ width:110, height:12, marginBottom:6 }} />
          <div className="pc__skel" style={{ width:80, height:9 }} />
        </div>
      </div>
    </td>
    {[60,50,60,60,50].map((w,j) => <td key={j}><div className="pc__skel" style={{ width:w, height:12, marginLeft:"auto" }} /></td>)}
  </tr>
));

const AmazonSkeleton = () => Array.from({ length: 5 }).map((_, i) => (
  <div className="pc__amz-skel" key={i}>
    <div className="pc__amz-skel-img" />
    <div className="pc__amz-skel-info" style={{ borderLeft:"1px solid rgba(10,85,86,0.07)" }}>
      <div className="pc__skel" style={{ height:15, width:"75%" }} />
      <div className="pc__skel" style={{ height:11, width:"50%" }} />
      <div className="pc__skel" style={{ height:11, width:"90%", marginTop:4 }} />
      <div className="pc__skel" style={{ height:11, width:"80%" }} />
      <div style={{ display:"flex", gap:8, marginTop:6 }}>
        <div className="pc__skel" style={{ height:22, width:60, borderRadius:50 }} />
        <div className="pc__skel" style={{ height:22, width:60, borderRadius:50 }} />
      </div>
    </div>
    <div className="pc__amz-skel-action" style={{ borderLeft:"1px solid rgba(10,85,86,0.07)" }}>
      <div className="pc__skel" style={{ height:24, width:"80%", marginLeft:"auto" }} />
      <div className="pc__skel" style={{ height:11, width:"60%", marginLeft:"auto" }} />
      <div className="pc__skel" style={{ height:34, width:"100%", borderRadius:10, marginTop:"auto" }} />
      <div className="pc__skel" style={{ height:30, width:"100%", borderRadius:10 }} />
    </div>
  </div>
));

/* ─── Amazon Card ─── */
const AmazonCard = ({ item, onView, onEdit }) => {
  const { product_data: pd, current_stock, type, product_uuid } = item;
  const stockVal = parseFloat(current_stock || 0);
  const isLow    = pd.inventory_tracked && stockVal <= pd.min_order_qty;
  const gstAmt   = (pd.rate * (pd.tax_percent || 0)) / 100;
  const total    = pd.rate + gstAmt;
  const valuation = pd.rate * stockVal;

  return (
    <div className={`pc__amz-card ${isLow ? "pc__amz-card--lowstock" : ""}`}>

      {/* Image */}
      <div className="pc__amz-img-col">
        <img className="pc__amz-img"
          src={pd.image_path ? `${imgurl}${pd.image_path}` : "https://via.placeholder.com/400x300?text=No+Image"}
          alt={pd.name} />
        <span className={`pc__amz-img-badge ${type==="Product" ? "pc__amz-img-badge--product" : "pc__amz-img-badge--service"}`}>{type}</span>
        {isLow && <span className="pc__amz-img-badge pc__amz-img-badge--low"><FiAlertTriangle size={9} /> Low Stock</span>}
      </div>

      {/* Info */}
      <div className="pc__amz-info">
        <div>
          <div className="pc__amz-name" onClick={() => onView(item)}>{pd.name}</div>
          <Stars rate={pd.rate} />
          <div className="pc__amz-meta" style={{ marginTop:8 }}>
            {pd.hsn_sac && <span className="pc__amz-meta-chip pc__amz-meta-chip--hsn"><FiTag size={10} /> HSN: {pd.hsn_sac}</span>}
            {pd.unit    && <span className="pc__amz-meta-chip pc__amz-meta-chip--unit"><FiPackage size={10} /> {pd.unit}</span>}
            {pd.tax_percent > 0 && <span className="pc__amz-meta-chip pc__amz-meta-chip--gst"><FiZap size={10} /> GST {pd.tax_percent}%</span>}
          </div>
        </div>

        {pd.description ? (
          <div className="pc__amz-desc">{pd.description}</div>
        ) : (
          <div className="pc__amz-desc" style={{ fontStyle:"italic" }}>No description provided.</div>
        )}

        <div className="pc__amz-stock-row">
          {type === "Service" ? (
            <span className="pc__amz-instock">✦ Always Available</span>
          ) : isLow ? (
            <span className="pc__amz-lowstock"><FiAlertTriangle size={12} /> Only {stockVal} left!</span>
          ) : (
            <span className="pc__amz-instock">✓ In Stock</span>
          )}
          {type === "Product" && (
            <span className="pc__amz-stock-qty">{stockVal} {pd.unit || "units"}</span>
          )}
          <span className="pc__amz-uuid"># {product_uuid}</span>
        </div>
      </div>

      {/* Price + Actions */}
      <div className="pc__amz-action-col">
        <div className="pc__amz-price-wrap">
          <div className="pc__amz-price-lbl">Base Price</div>
          <div className="pc__amz-price">{formatCurrency(pd.rate)}</div>
          {pd.tax_percent > 0 && (
            <div className="pc__amz-price-gst">
              + {formatCurrency(gstAmt)} GST = {formatCurrency(total)}
            </div>
          )}
        </div>

        <div className="pc__amz-btns">
          <button className="pc__amz-btn-view" onClick={() => onView(item)}>
            <FiMaximize2 size={13} /> View Details
          </button>
          <button className="pc__amz-btn-edit" onClick={() => onEdit(product_uuid)}>
            <FiEdit size={13} /> Edit Item
          </button>
        </div>

        {type === "Product" && stockVal > 0 && (
          <div className="pc__amz-valuation">
            Stock value: <span>{formatCurrency(valuation)}</span>
          </div>
        )}
      </div>

    </div>
  );
};

/* ─── Grid Card ─── */
const ProductCard = ({ item, onView, onEdit }) => {
  const { product_data: pd, current_stock, type, product_uuid } = item;
  const stockVal = parseFloat(current_stock || 0);
  const isLow    = pd.inventory_tracked && stockVal <= pd.min_order_qty;
  return (
    <div className={`pc__card ${isLow ? "pc__card--lowstock" : ""}`}>
      <div className="pc__img-wrap">
        <img className="pc__img"
          src={pd.image_path ? `${imgurl}${pd.image_path}` : "https://via.placeholder.com/400x250?text=No+Image"}
          alt={pd.name} />
        <span className={`pc__type-tag ${type==="Product" ? "pc__type-tag--product" : "pc__type-tag--service"}`}>{type}</span>
        {isLow && <span className="pc__lowstock-badge"><FiAlertTriangle size={9} /> Low</span>}
        <div className="pc__img-overlay">
          <button className="pc__overlay-btn pc__overlay-btn--view" onClick={() => onView(item)}><FiMaximize2 size={12} /> View</button>
          <button className="pc__overlay-btn pc__overlay-btn--edit" onClick={() => onEdit(product_uuid)}><FiEdit size={12} /> Edit</button>
        </div>
      </div>
      <div className="pc__card-body">
        <div>
          <div className="pc__prod-name" title={pd.name}>{pd.name}</div>
          {pd.hsn_sac && <div className="pc__prod-hsn">HSN: {pd.hsn_sac}</div>}
        </div>
        <div className="pc__prod-stats">
          <div className="pc__sg">
            <span className="pc__sg-lbl">Price</span>
            <span className="pc__sg-val pc__sg-val--teal">{formatCurrency(pd.rate)}</span>
          </div>
          <div className="pc__sg" style={{ textAlign:"right" }}>
            <span className="pc__sg-lbl">Stock</span>
            <span className={`pc__sg-val ${type==="Service" ? "pc__sg-val--muted" : isLow ? "pc__sg-val--danger" : ""}`}>
              {type==="Service" ? "∞" : `${stockVal} ${pd.unit||""}`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   MAIN
───────────────────────────────────────── */
function ProductCatelog() {
  const navigate = useNavigate();
  const [products, setProducts]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const token = localStorage.getItem("auth_token");

  const [searchTerm, setSearchTerm]   = useState("");
  const [typeFilter, setTypeFilter]   = useState("All");
  const [stockFilter, setStockFilter] = useState("All");
  const [sortBy, setSortBy]           = useState({ key:"name", direction:"asc" });
  const [viewMode, setViewMode]       = useState("amazon"); // grid | list | amazon
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = viewMode === "grid" ? 9 : viewMode === "amazon" ? 8 : 10;

  const [selectedItem, setSelectedItem]     = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink]           = useState("");
  const [copied, setCopied]                 = useState(false);
  const [toast, setToast]                   = useState(null);
  const [exporting, setExporting]           = useState(false);

  /* ── fetch (unchanged) ── */
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseurl}getAllProducts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) setProducts(res.data.data);
    } catch (err) { console.error(err); }
    finally { setTimeout(() => setLoading(false), 800); }
  };
  useEffect(() => { fetchProducts(); }, []);

  /* ── kpis (unchanged) ── */
  const kpis = useMemo(() => ({
    total:    products.length,
    value:    products.reduce((a, p) => a + (p.product_data.rate * (parseFloat(p.current_stock) || 0)), 0),
    lowStock: products.filter(p => p.type==="Product" && parseFloat(p.current_stock) <= p.product_data.min_order_qty).length,
  }), [products]);

  /* ── process (unchanged logic) ── */
  const processedProducts = useMemo(() => {
    let list = [...products];
    if (searchTerm.trim())
      list = list.filter(i =>
        i.product_data.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (i.product_data.hsn_sac||"").toLowerCase().includes(searchTerm.toLowerCase()));
    if (typeFilter !== "All") list = list.filter(i => i.type === typeFilter);
    if (stockFilter === "Low") list = list.filter(i => i.type==="Product" && parseFloat(i.current_stock) <= i.product_data.min_order_qty);
    if (stockFilter === "Tracked") list = list.filter(i => i.product_data.inventory_tracked);
    list.sort((a, b) => {
      let aV, bV;
      if (sortBy.key === "name")  { aV = a.product_data.name; bV = b.product_data.name; }
      else if (sortBy.key === "rate")  { aV = a.product_data.rate; bV = b.product_data.rate; }
      else if (sortBy.key === "stock") { aV = parseFloat(a.current_stock||0); bV = parseFloat(b.current_stock||0); }
      let cmp = typeof aV === "number" ? aV - bV : String(aV||"").localeCompare(String(bV||""));
      return sortBy.direction === "asc" ? cmp : -cmp;
    });
    return list;
  }, [products, searchTerm, typeFilter, stockFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(processedProducts.length / itemsPerPage));
  const paginated  = processedProducts.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

  const handleSort = (key) => {
    setSortBy(prev => ({ key, direction: prev.key===key && prev.direction==="asc" ? "desc" : "asc" }));
    setCurrentPage(1);
  };
  const SortIcon = ({ k }) => sortBy.key !== k
    ? <FiChevronsUp size={12} className="pc__sort-icon" />
    : sortBy.direction === "asc"
      ? <FiChevronsUp size={12} className="pc__sort-icon pc__sort-icon--on" />
      : <FiChevronsDown size={12} className="pc__sort-icon pc__sort-icon--on" />;

  const openDetail  = (item) => setSelectedItem(item);
  const closeDetail = () => setSelectedItem(null);

  const handleShare = () => {
    setShareLink(`${window.location.origin}/product-catalog?ref=share&t=${Date.now()}`);
    setShowShareModal(true);
  };
  const handleCopy = () => {
    navigator.clipboard.writeText(shareLink).then(() => { setCopied(true); setTimeout(()=>setCopied(false),2200); });
  };

  /* ── export CSV (unchanged) ── */
  const handleExport = () => {
    setExporting(true);
    try {
      const headers = ["Name","Type","HSN/SAC","Unit","Rate (₹)","GST %","Stock","Min Order Qty","Inventory Tracked","Description"];
      const rows = processedProducts.map(p => {
        const d = p.product_data;
        return [d.name, p.type, d.hsn_sac||"", d.unit||"", d.rate, d.tax_percent||0, parseFloat(p.current_stock||0), d.min_order_qty||1, d.inventory_tracked?"Yes":"No", (d.description||"").replace(/,/g,";")];
      });
      const csv = [headers,...rows].map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
      const url = URL.createObjectURL(new Blob([csv],{type:"text/csv;charset=utf-8;"}));
      const a = document.createElement("a"); a.href=url; a.download=`product-catalog-${new Date().toISOString().slice(0,10)}.csv`; a.click(); URL.revokeObjectURL(url);
      showToast("✓ Catalog exported successfully","success");
    } catch(e){ console.error(e); }
    finally { setTimeout(()=>setExporting(false),600); }
  };

  const showToast = (msg, type="") => { setToast({msg,type}); setTimeout(()=>setToast(null),2800); };

  /* VIEW ICON COMPONENT */
  const ViewIcon = ({ mode }) => {
    if (mode === "grid")   return <FiGrid size={14} />;
    if (mode === "list")   return <FiList size={14} />;
    if (mode === "amazon") return <FiShoppingCart size={14} />;
  };

  /* ═══ RENDER ═══ */
  return (
    <>
      <style>{CSS}</style>
      <Topbar /><Sidebar />

      <main className="main-wrapper">
        <div className="pc__wrap">

          {/* Header */}
          <div className="pc__hd">
            <div className="pc__hd-left">
              <div className="pc__hd-icon"><FiLayers /></div>
              <div>
                <div className="pc__hd-title">Inventory Catalog</div>
                <div className="pc__hd-sub">Track and manage your products and services</div>
              </div>
            </div>
            <div className="pc__hd-actions">
              <button className="pc__btn-outline pc__btn-outline--gold" onClick={handleShare}><FiShare2 size={14} /> Share Catalog</button>
              <button className="pc__btn-outline" onClick={handleExport} disabled={exporting}>
                {exporting ? <div className="pc__spinner" /> : <FiDownload size={14} />} Export CSV
              </button>
              <button className="pc__cta" onClick={() => navigate("/product-add")}><FiPlus size={14} /> New Item</button>
            </div>
          </div>

          {/* Stats */}
          <div className="pc__stats">
            {[
              { label:"Total Items",    value:kpis.total,  icon:<FiLayers size={18} />,       cls:"" },
              { label:"Stock Valuation",value:`₹${kpis.value.toLocaleString("en-IN",{maximumFractionDigits:0})}`, icon:<FiDollarSign size={18} />, cls:" " },
              { label:"Low Stock Items",value:kpis.lowStock, icon:<FiAlertTriangle size={18} />, cls:" " },
            ].map((kpi,i) => (
              <div className="pc__stat" key={i}>
                <div className={`pc__stat-icon${kpi.cls}`}>{kpi.icon}</div>
                <div>
                  <div className="pc__stat-lbl">{kpi.label}</div>
                  <div className="pc__stat-val">{loading ? <div className="pc__skel" style={{width:70,height:13,marginTop:4}} /> : kpi.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Controls */}
          <div className="pc__controls-bar">
            <div className="pc__controls-left">
              <div className="pc__search">
                <FiSearch className="pc__search-icon" />
                <input className="pc__search-input" placeholder="Search by name or HSN…" value={searchTerm}
                  onChange={e=>{setSearchTerm(e.target.value);setCurrentPage(1);}} />
                {searchTerm && <FiX size={13} style={{color:"var(--pc-muted)",cursor:"pointer"}} onClick={()=>{setSearchTerm("");setCurrentPage(1);}} />}
              </div>
              <select className="pc__type-select" value={typeFilter} onChange={e=>{setTypeFilter(e.target.value);setCurrentPage(1);}}>
                <option value="All">All Types</option>
                <option value="Product">Products</option>
                <option value="Service">Services</option>
              </select>
              {["All","Low","Tracked"].map(f => (
                <button key={f} className={`pc__filter-pill ${stockFilter===f?"pc__filter-pill--active":""}`}
                  onClick={()=>{setStockFilter(f);setCurrentPage(1);}}>
                  {f==="All" ? "All Stock" : f==="Low" ? "⚠ Low" : "Tracked"}
                </button>
              ))}
            </div>
            <div className="pc__controls-right">
              <span className="pc__results-count">{processedProducts.length} items</span>
              <div className="pc__view-toggle">
                {["grid","amazon","list"].map(m => (
                  <button key={m} title={m==="amazon" ? "Product listing view" : `${m} view`}
                    className={`pc__view-btn-toggle ${viewMode===m?"pc__view-btn-toggle--active":""}`}
                    onClick={()=>{setViewMode(m);setCurrentPage(1);}}>
                    <ViewIcon mode={m} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── GRID VIEW ── */}
          {viewMode === "grid" && (
            <div className="pc__grid">
              {loading ? Array.from({length:9}).map((_,i)=><GridSkeleton key={i} />) :
               paginated.length===0 ? (
                <div className="pc__empty"><div className="pc__empty-icon">🔍</div><div className="pc__empty-title">No items found</div><div className="pc__empty-sub">Try adjusting your search or filters.</div></div>
              ) : paginated.map(item=>(
                <ProductCard key={item.product_uuid} item={item} onView={openDetail} onEdit={uuid=>navigate(`/edit-product/${uuid}`)} />
              ))}
            </div>
          )}

          {/* ── AMAZON VIEW ── */}
          {viewMode === "amazon" && (
            <div className="pc__amz-wrap">
              {loading ? <AmazonSkeleton /> :
               paginated.length===0 ? (
                <div className="pc__empty" style={{gridColumn:"unset"}}><div className="pc__empty-icon">🔍</div><div className="pc__empty-title">No items found</div><div className="pc__empty-sub">Try adjusting your search or filters.</div></div>
              ) : paginated.map(item=>(
                <AmazonCard key={item.product_uuid} item={item} onView={openDetail} onEdit={uuid=>navigate(`/edit-product/${uuid}`)} />
              ))}
            </div>
          )}

          {/* ── LIST VIEW ── */}
          {viewMode === "list" && (
            <div className="pc__list-wrap">
              <table className="pc__list-table">
                <thead>
                  <tr>
                    <th onClick={()=>handleSort("name")} style={{paddingLeft:20}}>Product <SortIcon k="name"/></th>
                    <th>Type</th>
                    <th style={{textAlign:"right"}} onClick={()=>handleSort("rate")}>Price <SortIcon k="rate"/></th>
                    <th style={{textAlign:"center"}} onClick={()=>handleSort("stock")}>Stock <SortIcon k="stock"/></th>
                    <th style={{textAlign:"center"}}>Inventory</th>
                    <th style={{textAlign:"right",paddingRight:20}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? <ListSkeleton /> :
                   paginated.length===0 ? (
                    <tr><td colSpan="6"><div className="pc__empty"><div className="pc__empty-icon">🔍</div><div className="pc__empty-title">No items found</div></div></td></tr>
                   ) : paginated.map(item=>{
                    const pd=item.product_data, sv=parseFloat(item.current_stock||0), isLow=pd.inventory_tracked&&sv<=pd.min_order_qty;
                    return (
                      <tr key={item.product_uuid}>
                        <td style={{paddingLeft:20}}>
                          <div className="pc__list-prod-cell">
                            <img className="pc__list-img" src={pd.image_path?`${imgurl}${pd.image_path}`:"https://via.placeholder.com/40"} alt={pd.name} />
                            <div><div className="pc__list-name">{pd.name}</div><div className="pc__list-uuid">{item.product_uuid}</div></div>
                          </div>
                        </td>
                        <td><span className={`pc__type-badge ${item.type==="Product"?"pc__type-badge--product":"pc__type-badge--service"}`}>{item.type}</span></td>
                        <td><div className="pc__list-price">{formatCurrency(pd.rate)}</div></td>
                        <td>{item.type==="Service"?<div className="pc__list-stock pc__list-stock--na">N/A</div>:<div className={`pc__list-stock ${isLow?"pc__list-stock--low":"pc__list-stock--ok"}`}>{sv} <span style={{fontSize:11,fontWeight:400,color:"var(--pc-muted)"}}>{pd.unit}</span></div>}</td>
                        <td style={{textAlign:"center"}}>
                          <span style={{fontSize:11,fontFamily:"Sora,sans-serif",fontWeight:700,color:pd.inventory_tracked?"var(--pc-success)":"var(--pc-muted)",background:pd.inventory_tracked?"rgba(16,185,129,0.09)":"rgba(10,85,86,0.05)",border:pd.inventory_tracked?"1px solid rgba(16,185,129,0.22)":"1px solid var(--pc-border)",padding:"3px 9px",borderRadius:50}}>
                            {pd.inventory_tracked?"Tracked":"Not Tracked"}
                          </span>
                        </td>
                        <td style={{paddingRight:20}}>
                          <div className="pc__list-actions">
                            <button className="pc__list-action-btn pc__list-action-btn--view" title="View" onClick={()=>openDetail(item)}><FiMaximize2 size={13}/></button>
                            <button className="pc__list-action-btn pc__list-action-btn--edit" title="Edit" onClick={()=>navigate(`/edit-product/${item.product_uuid}`)}><FiEdit size={13}/></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && processedProducts.length > itemsPerPage && (
            <div className="pc__pagination">
              <button className="pc__pg-btn" disabled={currentPage===1} onClick={()=>setCurrentPage(p=>p-1)}><FiChevronLeft size={14}/></button>
              <div className="pc__pg-info">Page {currentPage} of {totalPages}</div>
              <button className="pc__pg-btn" disabled={currentPage>=totalPages} onClick={()=>setCurrentPage(p=>p+1)}><FiChevronRight size={14}/></button>
            </div>
          )}
        </div>
      </main>

      {/* Detail Modal */}
      {selectedItem && (
        <div className="pc__modal-overlay" onClick={e=>{if(e.target===e.currentTarget)closeDetail();}}>
          <div className="pc__modal">
            <div className="pc__modal-hd">
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span className={`pc__type-badge ${selectedItem.type==="Product"?"pc__type-badge--product":"pc__type-badge--service"}`}>{selectedItem.type}</span>
                <span style={{fontFamily:"Sora,sans-serif",fontWeight:800,fontSize:15,color:"var(--pc-text)"}}>{selectedItem.product_data.name}</span>
              </div>
              <button className="pc__modal-close" onClick={closeDetail}><FiX size={14}/></button>
            </div>
            <div className="pc__modal-body">
              <img className="pc__modal-img" src={selectedItem.product_data.image_path?`${imgurl}${selectedItem.product_data.image_path}`:"https://via.placeholder.com/400x300?text=No+Image"} alt={selectedItem.product_data.name} />
              <div className="pc__modal-info">
                {[
                  ["HSN / SAC", selectedItem.product_data.hsn_sac||"—"],
                  ["Unit",      selectedItem.product_data.unit||"—"],
                  ["Base Rate", formatCurrencyFull(selectedItem.product_data.rate)],
                  ["GST",       `${selectedItem.product_data.tax_percent||0}%`],
                  ["Current Stock", selectedItem.type==="Service" ? "∞" : `${parseFloat(selectedItem.current_stock||0)} ${selectedItem.product_data.unit||""}`],
                  ["Min Order Qty", selectedItem.product_data.min_order_qty||"1"],
                  ["Inv. Tracked",  selectedItem.product_data.inventory_tracked?"Yes":"No"],
                ].map(([lbl,val],i)=>(
                  <div className="pc__modal-row" key={i}>
                    <span className="pc__modal-lbl">{lbl}</span>
                    <span className={`pc__modal-val ${lbl==="Base Rate"?"pc__modal-val--teal":""}`}>{val}</span>
                  </div>
                ))}
                {selectedItem.product_data.description && (
                  <div style={{paddingTop:8}}>
                    <div style={{fontSize:10,fontFamily:"Sora,sans-serif",fontWeight:700,letterSpacing:"0.07em",textTransform:"uppercase",color:"var(--pc-muted)",marginBottom:5}}>Description</div>
                    <div style={{fontSize:12.5,color:"var(--pc-text)",lineHeight:1.6}}>{selectedItem.product_data.description}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="pc__modal-ft">
              <button className="pc__btn-outline" onClick={()=>{navigate(`/edit-product/${selectedItem.product_uuid}`);closeDetail();}}><FiEdit size={13}/> Edit Item</button>
              <button className="pc__cta" style={{padding:"9px 18px",fontSize:13}} onClick={closeDetail}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="pc__modal-overlay" onClick={e=>{if(e.target===e.currentTarget)setShowShareModal(false);}}>
          <div className="pc__share-modal">
            <div className="pc__modal-hd">
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:34,height:34,borderRadius:10,background:"linear-gradient(135deg,var(--pc-teal),var(--pc-teal-lite))",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff"}}><FiShare2 size={15}/></div>
                <div>
                  <div style={{fontFamily:"Sora,sans-serif",fontWeight:800,fontSize:14,color:"var(--pc-text)"}}>Share Catalog</div>
                  <div style={{fontSize:12,color:"var(--pc-muted)"}}>Share a link to your product catalog</div>
                </div>
              </div>
              <button className="pc__modal-close" onClick={()=>setShowShareModal(false)}><FiX size={14}/></button>
            </div>
            <div className="pc__share-body">
              <div style={{fontSize:12.5,color:"var(--pc-muted)",marginBottom:12}}>
                Anyone with this link can view the catalog. The link includes {processedProducts.length} items with current filters applied.
              </div>
              <div className="pc__share-link-row">
                <input className="pc__share-link" value={shareLink} readOnly />
                <button className={`pc__copy-btn ${copied?"pc__copy-btn--success":""}`} onClick={handleCopy}>
                  {copied?<FiCheck size={13}/>:<FiCopy size={13}/>} {copied?"Copied!":"Copy"}
                </button>
              </div>
              <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid var(--pc-border)",display:"flex",justifyContent:"flex-end"}}>
                <button className="pc__btn-outline" onClick={()=>setShowShareModal(false)}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`pc__toast ${toast.type==="success"?"pc__toast--success":""}`}>{toast.msg}</div>}
    </>
  );
}

export default ProductCatelog;
